import React, { FC, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import { qmrsService, techlineService } from '@web/services/singletons';
import { Account, Retailer } from '@packages/models/api';
import { Button, Typography } from '@packages/ui/shared';
import { useAuthState } from '@packages/contexts/auth';
import { getLogger } from '@packages/core/logging';
import AsyncPage from '@web/components/async-page';
import { Chip } from '@web/administration/components';
import { DetailHeader, DetailView, DetailViewBody, DetailViewContent } from '@web/components/detail-view';
import { formatToTitleCase } from '@web/utils';

const logger = getLogger('admin');

export const RetailerDetail: FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { account } = useAuthState();
    const { retailerId } = useParams<{ retailerId: string }>() as { retailerId: string };
    const [retailer, setRetailer] = useState<Retailer>();
    const [approvers, setApprovers] = useState<Account[]>([]);
    const [caseMembers, setCaseMembers] = useState<Account[]>([]);
    const retailerText = `Region-Zone-District: ${retailer?.regionCode}-${retailer?.zoneCode}-${retailer?.districtCode}`;
    const canViewCaseMemberForRetailer = account?.systemCapabilities.canViewCaseMemberForRetailer;
    const canViewFailCodeApprover = account?.systemCapabilities.canViewFailCodeApprover;

    const fetchData = useCallback(async () => {
        let promises = [];

        const retailerResultPromise = qmrsService.fetchRetailerbyId({ retailerId }).then((response) => {
            if (!response.success && response.aborted) {
                return;
            } else if (!response.success) {
                throw response.data;
            }

            setRetailer(response.data.retailer);
        });
        promises.push(retailerResultPromise);

        if (canViewFailCodeApprover) {
            const approverResultPromise = qmrsService
                .fetchRetailerFailCodeApprovers({ retailerId })
                .then((response) => {
                    if (!response.success && response.aborted) {
                        return;
                    } else if (!response.success) {
                        throw response.data;
                    }

                    setApprovers(response.data.accounts);
                })
                .catch((e) => {
                    logger.error(`Failed to fetch fail code approvers for retailer [${retailerId}]`, {
                        error: e,
                        retailerId,
                    });
                });
            promises.push(approverResultPromise);
        }

        if (canViewCaseMemberForRetailer) {
            const caseMemberResultPromise = techlineService
                .fetchRetailerCaseMembers({ retailerId })
                .then((response) => {
                    if (!response.success && response.aborted) {
                        return;
                    } else if (!response.success) {
                        throw response.data;
                    }
                    setCaseMembers(response.data.accounts);
                })
                .catch((e) => {
                    logger.error(`Failed to fetch case members for retailer [${retailerId}]`, {
                        error: e,
                        retailerId,
                    });
                });
            promises.push(caseMemberResultPromise);
        }

        return Promise.all(promises);
    }, [retailerId, canViewCaseMemberForRetailer, canViewFailCodeApprover]);

    function handleEditButtonPress() {
        if (!retailer) {
            return;
        }

        navigate(`/administration/retailers/${retailer.retailerId}/edit`);
    }

    const breadcrumbs = [
        {
            to: '/administration',
            title: t('administration:breadcrumbs.adminHub', 'Administration Hub'),
        },
        {
            to: '/administration/retailers',
            title: t('retailers:breadcrumbs.manageRetailers', 'Manage Retailers'),
        },
        {
            to: `/administration/retailers/${retailerId}`,
            title: t('retailers:breadcrumbs.retailer', 'Retailer'),
        },
    ];

    const rightSideItems = [
        <Typography color="textDarkSecondary">{retailerText}</Typography>,
        <Button onPress={handleEditButtonPress}>Edit Retailer</Button>,
    ];

    return (
        <AsyncPage fetchData={fetchData}>
            <DetailView>
                <DetailHeader
                    rightSideItems={rightSideItems}
                    breadcrumbs={breadcrumbs}
                    hasBack={true}
                    subtitle={`${retailer?.name} (#${retailer?.code})`}
                ></DetailHeader>
                <DetailViewContent topOffset="breadcrumb">
                    <DetailViewBody>
                        {canViewFailCodeApprover && (
                            <>
                                <Row className="mb-8">
                                    <Col>
                                        <Typography variant="h3">
                                            {t('retailers:manageRetailers.failCodeSettings.title')}
                                        </Typography>
                                    </Col>
                                </Row>
                                <Row className="mb-4">
                                    <Col xs={12}>
                                        <div className="mb-2">
                                            <Typography variant="label">
                                                {t('retailers:manageRetailers.failCodeSettings.approvalSetting')}
                                            </Typography>
                                        </div>
                                        <Typography>{retailer?.failCodeApproverSettingDescription}</Typography>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12}>
                                        <div className="mb-2">
                                            <Typography variant="label">
                                                {t('retailers:manageRetailers.failCodeSettings.authorizedApprovers')}
                                            </Typography>
                                        </div>
                                        <Row className="row-cols-auto">
                                            {approvers.length > 0 ? (
                                                approvers.map((approver) => {
                                                    return (
                                                        <Col key={approver.accountId} className="mb-2">
                                                            <Chip
                                                                title={formatToTitleCase(approver.name)}
                                                                styleProps={{ padding: '8px 12px' }}
                                                            />
                                                        </Col>
                                                    );
                                                })
                                            ) : (
                                                <Typography>
                                                    {t('retailers:manageRetailers.failCodeSettings.noApprovers')}
                                                </Typography>
                                            )}
                                        </Row>
                                    </Col>
                                </Row>
                            </>
                        )}
                        {canViewCaseMemberForRetailer && (
                            <>
                                <Row className="mb-8 mt-8">
                                    <Col>
                                        <Typography variant="h3">
                                            {t('retailers:manageRetailers.techlineSettings.title')}
                                        </Typography>
                                    </Col>
                                </Row>
                                <Row className="mb-4">
                                    <Col xs={12}>
                                        <div className="mb-2">
                                            <Typography variant="label">
                                                {t('retailers:manageRetailers.techlineSettings.description')}
                                            </Typography>
                                        </div>
                                        <Row className="row-cols-auto">
                                            {caseMembers.length > 0 ? (
                                                caseMembers.map((caseMember) => {
                                                    return (
                                                        <Col key={caseMember.accountId} className="mb-2">
                                                            <Chip
                                                                title={formatToTitleCase(caseMember.name)}
                                                                styleProps={{ padding: '8px 12px' }}
                                                            />
                                                        </Col>
                                                    );
                                                })
                                            ) : (
                                                <Typography>
                                                    {t('retailers:manageRetailers.techlineSettings.noUsers')}
                                                </Typography>
                                            )}
                                        </Row>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </DetailViewBody>
                </DetailViewContent>
            </DetailView>
        </AsyncPage>
    );
};
